<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <!-- Header -->
        <v-card-title>
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark>mdi-arrow-left</v-icon>
          </v-btn>
          Add a Hospital
        </v-card-title>

        <!-- Form -->
        <v-card-text>
          <v-row>
            <v-col sm="6">
              <v-text-field
                v-model="form.name"
                outlined
                label="Hospital Name *"
                prepend-inner-icon="mdi-hospital-building"
                :error-messages="errors.name"
                class="capitalize-input"
              />
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.email"
                outlined
                label="Email"
                prepend-inner-icon="mdi-email"
                :error-messages="errors.email"
              />
            </v-col>

            <v-col sm="6">
              <v-text-field
                v-model="form.phone"
                outlined
                label="Phone"
                type="number"
                prepend-inner-icon="mdi-phone"
                :error-messages="errors.phone"
                @input="form.phone = form.phone.toString().slice(0, 10)"
              />
            </v-col>

            <v-col sm="6">
              <v-text-field
                v-model="form.tagline"
                outlined
                label="Tagline (numeric)"
                prepend-inner-icon="mdi-tag"
                type="text"
                :error-messages="errors.tagline"
              />
            </v-col>

            <v-col sm="4">
              <v-select
                v-model="form.country_id"
                :items="countries"
                item-text="description"
                item-value="id"
                outlined
                label="Country"
                prepend-inner-icon="mdi-earth"
                :error-messages="errors.country_id"
                @change="onCountryChange"
              />
            </v-col>

            <v-col sm="4">
              <v-select
                v-model="form.state_id"
                :items="states"
                item-text="description"
                item-value="id"
                outlined
                label="State"
                prepend-inner-icon="mdi-map-outline"
                :error-messages="errors.state_id"
                :disabled="!form.country_id"
                @change="onStateChange"
              />
            </v-col>

            <v-col sm="4">
              <v-select
                v-model="form.city_id"
                :items="cities"
                item-text="description"
                item-value="id"
                outlined
                label="City"
                prepend-inner-icon="mdi-city"
                :error-messages="errors.city_id"
                :disabled="!form.state_id"
              />
            </v-col>

            <v-col sm="12">
              <v-textarea
                v-model="form.address"
                outlined
                label="Address"
                prepend-inner-icon="mdi-map-marker"
                rows="3"
                :error-messages="errors.address"
                class="capitalize-input"
              />
            </v-col>

            <!-- Logo Upload -->
            <v-col sm="6">
              <label class="mb-2">Hospital Logo</label>
              <br />
              <input
                type="file"
                ref="file"
                accept="image/*"
                @change="onFileChange"
              />

              <v-btn
                x-small
                class="reset_image my-1 ml-2"
                color="red"
                dark
                @click="removeLogo"
              >
                Remove
              </v-btn>

              <div v-if="logoPreview" class="mt-3">
                <label>Logo Preview</label>
                <br />
                <img
                  :src="logoPreview"
                  style="
                    height: 150px;
                    width: 150px;
                    object-fit: cover;
                    border-radius: 8px;
                    border: 1px solid #ddd;
                  "
                />
              </div>
            </v-col>

            <!-- Status -->
            <v-col sm="4">
              <v-switch
                class="mt-0 ml-5"
                v-model="form.is_active"
                inset
                :label="`Is Active: ${form.is_active ? 'YES' : 'NO'}`"
              />
            </v-col>
          </v-row>

          <!-- Save Button -->
          <v-row class="mt-4">
            <v-col>
              <v-btn
                :loading="isLoading"
                :disabled="isLoading"
                rounded
                color="primary"
                dark
                @click="save"
              >
                Save Hospital
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "HospitalCreatePage",
  data() {
    return {
      form: {
        id: null, // Ensure we pass ID when updating
        name: "",
        email: "",
        phone: "",
        address: "",
        country_id: null,
        state_id: null,
        city_id: null,
        tagline: null,
        is_active: true,
      },
      isLoading: false,
      logoPreview: null, // For preview
      file: null, // Actual file
      countries: [],
      states: [],
      cities: [],
    };
  },
  computed: {
    errors() {
      return this.$store.getters['errors/getErrors'];
    }
  },
  methods: {
    async save() {
      try {
        this.isLoading = true;
        // Transform name and address to uppercase before sending
        const formData = {
          ...this.form,
          name: this.form.name.toUpperCase(),
          address: this.form.address.toUpperCase(),
        };
        let hospitals = await axios.post("/hospitals", formData);
        this.form = hospitals.data.data;
        console.log("hospitals", this.hospitals);
        await this.handleFileUpload();
        await this.saveUserTimestamp(null, this.form);
        this.isLoading = false;
        this.$router.back();
      } catch (e) {
        // alert("error");
        console.log(e);
        this.isLoading = false;
      }
    },

    async handleFileUpload() {
      console.log("1");

      if (!this.file || !this.form.id) return;

      let formData = new FormData();
      formData.append("id", this.form.id);
      formData.append("logo_path", this.file);

      try {
        await axios.post("hospitals/upload_logo", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } catch (error) {
        console.error("Logo upload failed", error);
      }
    },

    onFileChange(e) {
      this.file = e.target.files[0];
      if (this.file) {
        this.logoPreview = URL.createObjectURL(this.file);
      }
    },

    removeLogo() {
      this.file = null;
      this.logoPreview = null;
      this.$refs.file.value = null;
    },

    async fetchMasters() {
      try {
        const response = await axios.get("/masters?context=hospital");
        this.countries = response.data.countries || [];
      } catch (error) {
        console.error("Error fetching masters:", error);
      }
    },

    async onCountryChange() {
      // Reset state and city when country changes
      this.form.state_id = null;
      this.form.city_id = null;
      this.states = [];
      this.cities = [];

      if (this.form.country_id) {
        try {
          const response = await axios.get(`/states/${this.form.country_id}`);
          this.states = response.data || [];
        } catch (error) {
          console.error("Error fetching states:", error);
        }
      }
    },

    async onStateChange() {
      // Reset city when state changes
      this.form.city_id = null;
      this.cities = [];

      if (this.form.state_id) {
        try {
          const response = await axios.get(`/cities/${this.form.state_id}`);
          this.cities = response.data || [];
        } catch (error) {
          console.error("Error fetching cities:", error);
        }
      }
    },
  },
  async mounted() {
    await this.fetchMasters();
  },
};
</script>

<style scoped>
.v-card {
  margin: 20px 0;
}

.capitalize-input >>> input,
.capitalize-input >>> textarea {
  text-transform: uppercase;
}
</style>
